/**
 * onboard-spec — encode/decode the prefill "spec" carried in the console
 * onboarding deep-link (`/onboard?spec=<base64url>`).
 *
 * The spec is UNTRUSTED prefill produced by the external `hotline-widget` skill
 * (scripts/build-onboard-link.mjs) or any caller: it only pre-populates the
 * onboarding form, which the authenticated user reviews and confirms. Nothing is
 * created by the link alone. So `decodeSpec` never trusts the input — it clamps
 * every field to safe bounds and drops anything malformed.
 *
 * The encoding mirrors the skill exactly: base64url of the compact JSON
 * (`Buffer.from(json).toString('base64url')` on the Node side ⇄ `btoa` here).
 */
import type { CaptureLevel } from '@hotline/contracts';

/** The decoded, sanitized onboarding prefill. */
export interface OnboardSpec {
  v: 1;
  /** Suggested widget-instance name (may be '' if the caller omitted it). */
  name: string;
  /** Suggested organization name — seeds registration for brand-new users. */
  organizationName?: string;
  /** Suggested allowed origins (deduped, capped). */
  allowedOrigins: string[];
  /** Suggested capture level (0–3), default 1. */
  captureLevel: CaptureLevel;
  /** Detected framework — display/telemetry only. */
  framework?: string;
  /** Provenance tag, e.g. "hotline-widget-skill@1". */
  source?: string;
}

/** Reject anything larger than this many base64url chars (URL-abuse guard). */
const MAX_SPEC_B64 = 4096;
const MAX_ORIGINS = 20;

// ── base64url ⇄ bytes (browser/jsdom via btoa/atob; UTF-8 via TextEncoder) ────

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(s: string): Uint8Array {
  let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad === 2) b64 += '==';
  else if (pad === 3) b64 += '=';
  else if (pad === 1) throw new Error('Invalid onboarding spec encoding.');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// ── sanitize ──────────────────────────────────────────────────────────────────

/** Trim + cap a candidate string; undefined when not a non-empty string. */
function asString(v: unknown, max: number): string | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  if (!t) return undefined;
  return t.length > max ? t.slice(0, max) : t;
}

/** Coerce to a valid capture level, defaulting to 1. */
function clampCaptureLevel(v: unknown): CaptureLevel {
  const n = typeof v === 'number' ? v : Number.parseInt(String(v), 10);
  return n === 0 || n === 1 || n === 2 || n === 3 ? n : 1;
}

/**
 * Coerce an arbitrary decoded value into a safe `OnboardSpec`. Never throws;
 * unknown/oversized fields are clamped or dropped. Exported for direct testing.
 */
export function sanitizeSpec(raw: unknown): OnboardSpec {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  const originsRaw = Array.isArray(o.allowedOrigins) ? o.allowedOrigins : [];
  const allowedOrigins = [
    ...new Set(
      originsRaw
        .map((x) => asString(x, 2048))
        .filter((x): x is string => Boolean(x)),
    ),
  ].slice(0, MAX_ORIGINS);

  const organizationName = asString(o.organizationName, 256);
  const framework = asString(o.framework, 64);
  const source = asString(o.source, 64);

  return {
    v: 1,
    name: asString(o.name, 256) ?? '',
    ...(organizationName ? { organizationName } : {}),
    allowedOrigins,
    captureLevel: clampCaptureLevel(o.captureLevel),
    ...(framework ? { framework } : {}),
    ...(source ? { source } : {}),
  };
}

// ── public API ──────────────────────────────────────────────────────────────

/** Encode a spec into the base64url token used in `?spec=`. */
export function encodeSpec(spec: OnboardSpec): string {
  return bytesToBase64Url(new TextEncoder().encode(JSON.stringify(spec)));
}

/** Decode + sanitize a `?spec=` token. Throws on missing/oversized/bad input. */
export function decodeSpec(token: string): OnboardSpec {
  if (typeof token !== 'string' || token.length === 0 || token.length > MAX_SPEC_B64) {
    throw new Error('Onboarding spec is missing or too large.');
  }
  const json = new TextDecoder().decode(base64UrlToBytes(token));
  return sanitizeSpec(JSON.parse(json) as unknown);
}

/** Non-throwing decode for UI use (missing token → a friendly error result). */
export function safeDecodeSpec(
  token: string | null | undefined,
): { ok: true; spec: OnboardSpec } | { ok: false; error: string } {
  if (!token) return { ok: false, error: 'No onboarding details in this link.' };
  try {
    return { ok: true, spec: decodeSpec(token) };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Invalid onboarding link.',
    };
  }
}
